import { createSelector } from 'reselect'

import type { ExperienceListFilters } from 'actions/ExperienceActions'
import buildLoyaltyLoungePassFilters from 'lib/experiences/buildLoyaltyLoungePassFilters'
import { isSpoofed } from 'selectors/isSpoofed'
import { getClosestAirport } from 'selectors/flightsSelectors'
import getTourV2ItemViews from 'checkout/selectors/view/itemViews/getTourV2ItemViews'
import getHotelItemViews from 'checkout/selectors/view/itemViews/getHotelItemViews'
import getBedbankItemViews from 'checkout/selectors/view/itemViews/getBedbankItemViews'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getFlightItemsView } from 'checkout/selectors/view/flights'

/**
 * Leaf selector for loyalty lounge-pass checkout filters.
 * Kept out of experienceSelectors so getCanRedeemLoungePassLoyaltyBenefit can use it
 * without the experienceSelectors ↔ getCanRedeemLoungePassLoyaltyBenefit cycle.
 */
const getLoyaltyCheckoutLoungePassFilters = createSelector(
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getClosestAirport(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.auth.account.recentlyUsedAirportCode,
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.experience.experienceLists,
  (
    bedbankViews,
    tourV2Views,
    hotelViews,
    villaViews,
    flightViews,
    closestAirport,
    isSpoofed,
    recentlyUsedAirportCode,
    airports,
    regionCode,
    experiences,
    experienceLists,
  ): Array<ExperienceListFilters> => {
    const bedbankViewsDataValid = bedbankViews.hasRequiredData
    const tourV2ViewsDataValid = tourV2Views.hasRequiredData
    const hotelViewsDataValid = hotelViews.hasRequiredData
    const villaViewsDataValid = villaViews.hasRequiredData
    const flightViewsDataValid = flightViews.hasRequiredData

    if (!bedbankViewsDataValid || !tourV2ViewsDataValid || !hotelViewsDataValid || !villaViewsDataValid || !flightViewsDataValid) return []

    const filters = buildLoyaltyLoungePassFilters({
      bedbankItems: bedbankViews.data,
      tourItems: tourV2Views.data,
      hotelItems: hotelViews.data,
      villaItemsViews: villaViews.data,
      flightItems: flightViews.data,
      airports,
      experiences,
      experienceLists,
      isSpoofed,
      regionCode,
      recentlyUsedAirportCode,
      closestAirport,
    })

    return filters
  },
)

export default getLoyaltyCheckoutLoungePassFilters
